import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { KeyRound, Trash2, RefreshCw, Copy, Check } from "lucide-react";
import { API } from "../../utils/helpers";

const PAGE_LABELS = { default: "Ana Sayfa", "1": "Ufuktepe", "2": "Dutluk" };
const pageLabel = (p) => PAGE_LABELS[p] || `Şube /${p}`;

const fmt = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const ErisimKodlariTab = ({ auth }) => {
  const [name, setName] = useState("");
  const [pending, setPending] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newCode, setNewCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/access/admin/list`, { auth });
      if (res.data?.success) {
        setPending(res.data.pending || []);
        setDevices(res.data.devices || []);
        setError("");
      } else {
        setError(res.data?.error || "Liste alınamadı.");
      }
    } catch { setError("Bağlantı hatası."); }
    finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    const n = name.trim();
    if (!n) { setError("Önce bir isim yazın (örn. Eymen'in cihazı)."); return; }
    setGenerating(true);
    setError("");
    try {
      const res = await axios.post(`${API}/access/admin/generate`, { auth, name: n });
      if (res.data?.success) {
        setNewCode({ code: res.data.code, name: n });
        setCopied(false);
        setName("");
        load();
      } else {
        setError(res.data?.error || "Kod üretilemedi.");
      }
    } catch { setError("Bağlantı hatası."); }
    finally { setGenerating(false); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(newCode.code); setCopied(true); } catch {}
  };

  const removeDevice = async (id) => {
    if (!window.confirm("Bu cihazı kaldırmak istiyor musunuz? Tekrar girmek için yeni kod gerekecek.")) return;
    try {
      await axios.post(`${API}/access/admin/remove-device`, { auth, id });
      load();
    } catch {}
  };

  const deleteCode = async (id) => {
    try {
      await axios.post(`${API}/access/admin/delete-code`, { auth, id });
      load();
    } catch {}
  };

  return (
    <div style={{ padding: "18px", maxWidth: 820, margin: "0 auto" }} data-testid="erisim-kodlari-tab">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <KeyRound size={22} color="#DA9F54" />
        <h2 style={{ color: "#DA9F54", margin: 0, fontSize: 20 }}>Erişim Kodları</h2>
      </div>
      <p style={{ color: "var(--adm-muted, #9fb3a8)", fontSize: 13, marginTop: 0, marginBottom: 18, lineHeight: 1.5 }}>
        Şube sayfaları (/1, /2, …) kilitlidir. Buradan tek seferlik 6 haneli kod üretin, bir isim atayın ve o kişiye verin.
        Kod bir sayfada bir kez girildiğinde o cihaz, siz kaldırana kadar o sayfaya kodsuz girebilir.
      </p>

      {/* Kod üret */}
      <div style={box}>
        <label style={lbl}>Yeni Kod — Cihaz / Kişi İsmi</label>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Örn. Eymen'in cihazı"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && generate()}
            style={inp}
            data-testid="access-name-input"
          />
          <button onClick={generate} disabled={generating} style={{ ...btnGold, opacity: generating ? 0.6 : 1 }} data-testid="access-generate-btn">
            {generating ? "Üretiliyor..." : "Kod Al"}
          </button>
        </div>

        {newCode && (
          <div style={codeBanner} data-testid="access-new-code">
            <div>
              <div style={{ fontSize: 12, color: "#9fb3a8" }}>{newCode.name}</div>
              <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: 8, color: "#DA9F54" }}>{newCode.code}</div>
            </div>
            <button onClick={copyCode} style={copyBtn} data-testid="access-copy-btn">
              {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Kopyalandı" : "Kopyala"}
            </button>
          </div>
        )}
      </div>

      {error && <p style={{ color: "#f87171", fontSize: 13 }} data-testid="access-tab-error">{error}</p>}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "24px 0 10px" }}>
        <h3 style={h3}>Bekleyen Kodlar <span style={badge}>{pending.length}</span></h3>
        <button onClick={load} style={refreshBtn} data-testid="access-refresh-btn"><RefreshCw size={15} /> Yenile</button>
      </div>
      <p style={{ color: "var(--adm-muted, #9fb3a8)", fontSize: 12, margin: "0 0 10px" }}>
        Henüz kullanılmamış kodlar. 6 saat içinde kullanılmazsa otomatik silinir.
      </p>
      <div style={tableWrap}>
        {pending.length === 0 ? (
          <div style={empty}>Bekleyen kod yok.</div>
        ) : pending.map((c) => (
          <div key={c.id} style={rowS} data-testid={`pending-row-${c.id}`}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, letterSpacing: 4, color: "#DA9F54", fontSize: 18 }}>{c.code}</div>
              <div style={{ fontSize: 13, color: "var(--adm-text,#cbd5c7)" }}>{c.name}</div>
            </div>
            <div style={{ fontSize: 12, color: "#9fb3a8", marginRight: 12 }}>{fmt(c.created_at)}</div>
            <button onClick={() => deleteCode(c.id)} style={iconBtn} title="Kodu sil" data-testid={`delete-code-${c.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <h3 style={{ ...h3, margin: "24px 0 10px" }}>Yetkili Cihazlar <span style={badge}>{devices.length}</span></h3>
      <div style={tableWrap}>
        {devices.length === 0 ? (
          <div style={empty}>Henüz yetkili cihaz yok.</div>
        ) : devices.map((d) => (
          <div key={d.id} style={rowS} data-testid={`device-row-${d.id}`}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: "var(--adm-text,#e5e7eb)", fontSize: 15 }}>{d.name}</div>
              <div style={{ fontSize: 12, color: "#9fb3a8" }}>
                Sayfa: <span style={{ color: "#DA9F54" }}>{pageLabel(d.profile)}</span> · Son görülme: {fmt(d.last_seen)}
              </div>
            </div>
            <button onClick={() => removeDevice(d.id)} style={{ ...btnGold, background: "#b91c1c", color: "#fff" }} data-testid={`remove-device-${d.id}`}>
              <Trash2 size={14} /> Kaldır
            </button>
          </div>
        ))}
      </div>
      {loading && <p style={{ color: "#9fb3a8", fontSize: 12, marginTop: 10 }}>Yükleniyor...</p>}
    </div>
  );
};

const box = { background: "var(--adm-card, #16201b)", border: "1px solid var(--adm-border, #2c3a33)", borderRadius: 14, padding: 18, marginBottom: 6 };
const lbl = { display: "block", color: "var(--adm-muted,#9fb3a8)", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 };
const inp = { flex: 1, minWidth: 200, padding: "12px 14px", fontSize: 15, background: "var(--adm-input,#0c1812)", color: "var(--adm-text,#fff)", border: "1px solid var(--adm-border,#2c3a33)", borderRadius: 10, outline: "none" };
const btnGold = { display: "inline-flex", alignItems: "center", gap: 6, padding: "12px 20px", fontSize: 15, fontWeight: 700, color: "#0a1510", background: "#DA9F54", border: "none", borderRadius: 10, cursor: "pointer" };
const codeBanner = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 16, padding: "14px 18px", background: "rgba(218,159,84,0.1)", border: "1px solid rgba(218,159,84,0.4)", borderRadius: 12 };
const copyBtn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "#DA9F54", background: "transparent", border: "1px solid #DA9F54", borderRadius: 10, cursor: "pointer" };
const h3 = { color: "var(--adm-text,#e5e7eb)", margin: 0, fontSize: 16, display: "flex", alignItems: "center", gap: 8 };
const badge = { background: "#DA9F54", color: "#0a1510", fontSize: 12, fontWeight: 800, padding: "1px 8px", borderRadius: 999 };
const refreshBtn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", fontSize: 13, color: "#DA9F54", background: "transparent", border: "1px solid #2c3a33", borderRadius: 10, cursor: "pointer" };
const tableWrap = { display: "flex", flexDirection: "column", gap: 8 };
const rowS = { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "var(--adm-card,#16201b)", border: "1px solid var(--adm-border,#2c3a33)", borderRadius: 10 };
const iconBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, color: "#f87171", background: "transparent", border: "1px solid #2c3a33", borderRadius: 8, cursor: "pointer" };
const empty = { padding: "18px", textAlign: "center", color: "#9fb3a8", fontSize: 13, background: "var(--adm-card,#16201b)", border: "1px dashed #2c3a33", borderRadius: 10 };

export default ErisimKodlariTab;
